// Reading a saved batch again from its original files: CapturePoint's Retry and Reprocess, which
// its main process sends to the engine as one command (`reprocess-reviewed`, apps/desktop/src/main/
// main.ts) with the same arguments. The screen reaches for them on "Read it again" when a page was
// never read, and whenever a job improvement is applied to (or undone on) the current batch:
// "Job improved · untouched values refreshed; corrections kept". Both were refused in Paige, so a
// page that was never read could not be read, and every applied improvement ended in "this batch
// could not be re-evaluated". The engine host keeps a batch's originals with the job's imports and
// runs the re-read as it runs a `process` (services/Paige.Engine.Host/StoredBatchWork.cs).
import type { ProcessResult } from '../shared/desktop-api';
import type { EngineCall } from './desktop-commands';
import { followRun, isTicket } from './process-runs';

/** What the screen sends: CapturePoint's `RetryRequest` and `ReviewedReprocessRequest` (one shape). */
export interface BatchReprocessRequest {
  jobId: string;
  batchId: string;
  documentTypes: unknown;
  separationMode?: unknown;
  separationBarcodeText?: unknown;
  boundaryOpeningProfiles?: unknown;
  boundaryContinuationProfiles?: unknown;
}

/** The engine's `reprocess-reviewed`, followed to its reading through `reprocess-status`. */
export async function reprocessBatch(
  call: EngineCall,
  request: BatchReprocessRequest,
  wait?: (ms: number) => Promise<void>,
): Promise<ProcessResult> {
  const first = await call<ProcessResult | unknown>('reprocess-reviewed', {
    jobId: request.jobId,
    batchId: request.batchId,
    documentTypes: request.documentTypes,
    separationMode: request.separationMode,
    separationBarcodeText: request.separationBarcodeText,
    boundaryOpeningProfiles: request.boundaryOpeningProfiles,
    boundaryContinuationProfiles: request.boundaryContinuationProfiles,
  });
  if (!isTicket(first)) return first as ProcessResult;
  return followRun<ProcessResult>(call, first, () => undefined, wait, 'reprocess-status');
}

export function batchReprocess(call: EngineCall): Record<string, unknown> {
  const reprocess = (request: BatchReprocessRequest) => reprocessBatch(call, request);
  return { retryDocuments: reprocess, reprocessReviewed: reprocess };
}
